import { useEffect, useRef, useState } from "react";
import {
  type Live as LiveData,
  type LiveRange,
  type LiveSeries,
  type Today as TodayData,
  fetchLive,
  fetchLiveSeries,
  fetchToday,
} from "../api";
import { usePoll } from "../usePoll";
import { EnergyFlow } from "../components/EnergyFlow";
import { LineChart } from "../components/LineChart";
import {
  type DetailMetric,
  MetricSeriesCard,
} from "../components/MetricSeriesCard";
import { Modal } from "../components/Modal";
import { RangeSelector } from "../components/RangeSelector";
import { TodayDetailTiles } from "../components/TodayTiles";
import { ScreenHeader, SectionTitle, Skeleton } from "../components/ui";

const DETAIL_LABELS: Record<DetailMetric, string> = {
  pv: "Opgewekt",
  load: "Verbruikt",
  battery_in: "Opgeladen",
  battery_out: "Ontladen",
  grid_in: "Geïmporteerd",
  grid_out: "Geëxporteerd",
  ev: "Auto geladen",
};

/**
 * Live tab: SOC ring + state pill and the energy flow (polled at 5 s,
 * balloons from /api/today), plus the recent-power chart from
 * /api/live/series with a 15m/1h/6h/24h range picker. Inside a range
 * the chart polls proportionally to the step (SERIES_POLL_MS) so it
 * slides instead of re-rendering every 5 s; a sequence counter
 * discards late responses when the user flips ranges quickly.
 * docs/customer-app.md > Front-end > Live tab polling
 */
const SERIES_POLL_MS: Record<LiveRange, number> = {
  "15m": 10_000,
  "1h":  30_000,
  "6h":  5 * 60_000,
  "24h": 30 * 60_000,
};

export function Live() {
  // Live + vandaag via usePoll: fout wist zichzelf bij succes, één blip
  // (achtergrond/hervatten) alarmeert niet, en er wordt niet gepold
  // terwijl het scherm weg is - src/usePoll.ts.
  const { data: live, error: liveErr } = usePoll<LiveData>(fetchLive, 5_000);
  const { data: today } = usePoll<TodayData>(fetchToday, 60_000);
  const [series, setSeries] = useState<LiveSeries | null>(null);
  // 6h covers a typical solar day's PV curve at one glance - better
  // landing view than 15m's near-flat noise for most customers.
  const [range, setRange] = useState<LiveRange>("6h");
  const [seriesErr, setSeriesErr] = useState<string | null>(null);
  const error = liveErr ?? seriesErr;
  const seriesSeq = useRef(0);
  // Ref, geen state: de poll-closure ververst alleen bij range-wissel
  // en zou anders een verouderde "hebben we data"-waarde lezen.
  const hasSeries = useRef(false);
  const [detailMetric, setDetailMetric] = useState<DetailMetric | null>(null);


  // Series polling - re-arms on range change.
  useEffect(() => {
    let cancelled = false;
    async function pollSeries() {
      const my = ++seriesSeq.current;
      try {
        const s = await fetchLiveSeries(range);
        if (!cancelled && my === seriesSeq.current) {
          setSeries(s);
          hasSeries.current = true;
          setSeriesErr(null);          // hersteld -> balk weg
        }
      } catch (e) {
        // Alleen alarmeren als we nog niets te tonen hebben; een blip
        // bij hervatten overschrijft geen werkende grafiek.
        if (!cancelled && !hasSeries.current) {
          setSeriesErr(e instanceof Error ? e.message : String(e));
        }
      }
    }
    pollSeries();
    const t = window.setInterval(pollSeries, SERIES_POLL_MS[range]);
    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  return (
    <div className="tab-enter stagger px-4 pt-6 pb-28 max-w-3xl mx-auto flex flex-col gap-5 lg:max-w-6xl lg:px-8 lg:pt-10 lg:pb-12 lg:grid lg:grid-cols-2 lg:items-start">
      <div className="lg:col-span-2">
        <ScreenHeader title="Live" subtitle="Wat gebeurt er nu" />
      </div>

      {error && (
        <div className="text-sm text-danger bg-danger-soft rounded-xl p-3 border border-card-border lg:col-span-2">
          {error}
        </div>
      )}

      {/* SOC has moved INTO the EnergyFlow card's Battery balloon -
          the dedicated big ring is overkill for the detail view.
          Op lg: hero links, vermogen-grafiek ernaast. */}
      <div className="lg:row-span-2">
      {live ? (
        <EnergyFlow live={live} today={today} />
      ) : (
        <Skeleton className="h-[420px] rounded-3xl" />
      )}
      </div>

      <section className="card p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs uppercase tracking-wider text-ink-mute">
            Vermogen laatste {rangeLabel(range)}
          </div>
          <RangeSelector value={range} onChange={setRange} />
        </div>
        {series ? (
          <>
            <LineChart
              t={series.t}
              height={220}
              series={[
                // PV + load are unidirectional - magnitude is the
                // whole story; no sign noise.
                { name: "Zon",      color: "var(--color-sun)",    values: series.pv_w },
                { name: "Huis",     color: "var(--color-home)",   values: series.load_w },
                // Battery + grid have meaningful direction - tooltip
                // renders these with ↓/↑ arrows so the user can tell
                // charging vs discharging / import vs export at a glance.
                { name: "Batterij", color: "var(--color-accent)", values: series.battery_w, directional: true },
                { name: "Net",      color: "var(--color-grid)",   values: series.grid_w,    directional: true },
              ]}
            />
            <Legend />
          </>
        ) : (
          <Skeleton className="h-[220px] rounded-xl" />
        )}
      </section>

      {/* PV-only + Load-only curves - same component the Home modal
          opens to. Inline here so customers who want to see just one
          series at a time (without battery + grid visual noise) can. */}
      <MetricSeriesCard metric="pv"   initialRange="6h" />
      <MetricSeriesCard metric="load" initialRange="6h" />

      {/* Battery + grid kWh detail - moved here from Home so the
          Overview tab stays glanceable. */}
      <section className="flex flex-col gap-3 lg:col-span-2">
        <SectionTitle>Tot nu toe vandaag</SectionTitle>
        {today ? (
          <TodayDetailTiles today={today} onMetricTap={setDetailMetric} />
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
        )}
      </section>

      {/* Tap any of the 6 metric tiles → modal with its bar chart.
          Reuses the same MetricSeriesCard the inline PV/Load cards
          above use; the modal title comes from DETAIL_LABELS. */}
      <Modal
        open={detailMetric !== null}
        onClose={() => setDetailMetric(null)}
        title={detailMetric ? DETAIL_LABELS[detailMetric] : ""}
      >
        {detailMetric && (
          <MetricSeriesCard metric={detailMetric} initialRange="6h" chartMode="bar" />
        )}
      </Modal>
    </div>
  );
}

function rangeLabel(r: LiveRange): string {
  return { "15m": "15 min", "1h": "uur", "6h": "6 uur", "24h": "24 uur" }[r];
}

function Legend() {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-mute">
      <Swatch color="var(--color-sun)" label="Zon" />
      <Swatch color="var(--color-home)" label="Huis" />
      <Swatch color="var(--color-accent)" label="Batterij" />
      <Swatch color="var(--color-grid)" label="Net" />
    </div>
  );
}

function Swatch({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        aria-hidden
        className="inline-block w-3 h-0.5 rounded-full"
        style={{ backgroundColor: color }}
      />
      {label}
    </span>
  );
}

